import type { StreamTargetChunk } from 'mediabunny';
import { parseSavedCuts } from '@/core/recording/session';
import type { TimeRange } from '@/core/time/ranges';
import type { WriterMessage, WriterReply } from './chunk-writer.worker';

/**
 * Recordings live in the Origin Private File System, one directory per session:
 * `raw.mp4` while recording (written as it happens, so a crash loses at most a
 * second), then `screen.mp4` and `camera.mp4` once it is prepared for editing.
 * A Web Lock marks the sessions an open tab is using, so other tabs (and the
 * next visit) can tell an active session from one left behind.
 */
const ROOT = 'recordings';
export const RAW_FILE = 'raw.mp4';
export const SCREEN_FILE = 'screen.mp4';
export const CAMERA_FILE = 'camera.mp4';
/** Retakes thrown away while recording, so a recovered recording opens without them too. */
const EDITS_FILE = 'edits.json';
/** An empty marker file: the recording was exported, so a leftover needn't be offered back. */
const EXPORTED_MARK = 'exported';
/** An empty marker file: a saved project uses the recording, which stays until the project is deleted. */
const PROJECT_MARK = 'project';
const lockName = (id: string) => `shycut-recording:${id}`;

const hasOpfs = (): boolean => typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function';

async function recordingsRoot(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(ROOT, { create: true });
}

/** Holds a Web Lock until the returned function is called; null when another tab holds it. */
async function acquireLock(name: string): Promise<(() => void) | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return () => undefined;
  return new Promise((resolve) => {
    void locks.request(name, { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolve(null);
        return undefined;
      }
      return new Promise<void>((release) => resolve(release));
    });
  });
}

async function heldLocks(): Promise<Set<string>> {
  const snapshot = await navigator.locks?.query().catch(() => null);
  return new Set((snapshot?.held ?? []).map((lock) => lock.name ?? ''));
}

const fileIn = async (directory: FileSystemDirectoryHandle, name: string): Promise<File | null> => {
  try {
    return await (await directory.getFileHandle(name)).getFile();
  } catch {
    return null;
  }
};

/** Where the recording's bytes go while it is made. */
export interface RecordingSink {
  readonly writable: WritableStream<StreamTargetChunk>;
  /** Bytes written so far. */
  readonly size: () => number;
  /** Flushes and closes; resolves once everything is on disk. */
  close(): Promise<void>;
  /** Rejects when writing failed (storage full); never resolves otherwise. */
  readonly failed: Promise<never>;
}

/** On-disk sink through the writer worker. */
async function opfsSink(path: readonly string[]): Promise<RecordingSink> {
  const worker = new Worker(new URL('./chunk-writer.worker.ts', import.meta.url), { type: 'module', name: 'shycut-recording-writer' });
  let size = 0;
  let fail: (error: Error) => void = () => undefined;
  const failed = new Promise<never>((_, reject) => (fail = reject));
  failed.catch(() => undefined);
  const waitFor = (type: 'opened' | 'closed') =>
    new Promise<void>((resolve, reject) => {
      const listener = ({ data }: MessageEvent<WriterReply>) => {
        if (data.type === 'error') reject(Object.assign(new Error(data.message), { name: data.name }));
        else if (data.type === type) resolve();
        else return;
        worker.removeEventListener('message', listener);
      };
      worker.addEventListener('message', listener);
    });
  worker.addEventListener('message', ({ data }: MessageEvent<WriterReply>) => {
    if (data.type === 'error') fail(Object.assign(new Error(data.message), { name: data.name }));
  });
  const send = (message: WriterMessage, transfer: Transferable[] = []) => worker.postMessage(message, transfer);
  const opened = waitFor('opened');
  send({ type: 'open', path });
  try {
    await opened;
  } catch (error) {
    worker.terminate();
    throw error;
  }
  return {
    writable: new WritableStream<StreamTargetChunk>({
      write(chunk) {
        // The muxer may reuse its buffer, so a copy is handed over (and transferred, not cloned again).
        const data = chunk.data.slice();
        size = Math.max(size, chunk.position + data.byteLength);
        send({ type: 'write', position: chunk.position, data }, [data.buffer]);
      },
    }),
    size: () => size,
    async close() {
      const closed = waitFor('closed');
      send({ type: 'close' });
      try {
        await closed;
      } finally {
        worker.terminate();
      }
    },
    failed,
  };
}

/** In-memory sink for browsers without OPFS: works, but doesn't survive a crash. */
function memorySink(onFinish: (file: Blob) => void): RecordingSink {
  const chunks: Array<{ position: number; data: Uint8Array }> = [];
  let size = 0;
  return {
    writable: new WritableStream<StreamTargetChunk>({
      write(chunk) {
        chunks.push({ position: chunk.position, data: chunk.data.slice() });
        size = Math.max(size, chunk.position + chunk.data.byteLength);
      },
    }),
    size: () => size,
    async close() {
      const buffer = new Uint8Array(size);
      for (const chunk of chunks) buffer.set(chunk.data, chunk.position);
      chunks.length = 0;
      onFinish(new Blob([buffer], { type: 'video/mp4' }));
    },
    failed: new Promise<never>(() => undefined),
  };
}

const randomSuffix = () => Math.random().toString(36).slice(2, 6);
/** `recording-2026-10-14-2155-k3f9` → `recording-2026-10-14-2155` */
const displayName = (id: string) => id.replace(/-[a-z0-9]{4}$/, '');

/** One recording on this device, from the first byte until it is deleted. */
export class RecordingSession {
  private memoryRaw: Blob | null = null;
  private memoryCuts: readonly TimeRange[] = [];

  private constructor(
    readonly id: string,
    private readonly directory: FileSystemDirectoryHandle | null,
    private unlock: () => void,
  ) {}

  /** Starts a new session (on disk where possible). */
  static async create(name: string): Promise<RecordingSession> {
    const id = `${name}-${randomSuffix()}`;
    if (!hasOpfs()) return new RecordingSession(id, null, () => undefined);
    try {
      const directory = await (await recordingsRoot()).getDirectoryHandle(id, { create: true });
      const unlock = (await acquireLock(lockName(id))) ?? (() => undefined);
      return new RecordingSession(id, directory, unlock);
    } catch {
      return new RecordingSession(id, null, () => undefined);
    }
  }

  /** Re-opens a session left behind, unless another tab is using it. */
  static async resume(id: string): Promise<RecordingSession | null> {
    if (!hasOpfs()) return null;
    const unlock = await acquireLock(lockName(id));
    if (!unlock) return null;
    try {
      const directory = await (await recordingsRoot()).getDirectoryHandle(id);
      return new RecordingSession(id, directory, unlock);
    } catch {
      unlock();
      return null;
    }
  }

  /** The file name people see: `recording-2026-10-14-2155`. */
  get name(): string {
    return displayName(this.id);
  }

  /** True when the recording is written to disk as it happens (and survives a crash). */
  get durable(): boolean {
    return this.directory !== null;
  }

  /** Path of the session directory inside OPFS, for workers; null when kept in memory. */
  get path(): readonly string[] | null {
    return this.directory ? [ROOT, this.id] : null;
  }

  async openSink(): Promise<RecordingSink> {
    if (this.directory) {
      try {
        return await opfsSink([ROOT, this.id, RAW_FILE]);
      } catch {
        // OPFS exists but sync handles don't (older Safari): fall back to memory below.
      }
    }
    return memorySink((blob) => (this.memoryRaw = blob));
  }

  async raw(): Promise<File | null> {
    if (this.memoryRaw) return new File([this.memoryRaw], `${this.name}.mp4`, { type: 'video/mp4' });
    return this.directory ? fileIn(this.directory, RAW_FILE) : null;
  }

  /** The prepared files, when the session got that far. */
  async prepared(): Promise<{ readonly screen: File; readonly camera: File | null } | null> {
    if (!this.directory) return null;
    const screen = await fileIn(this.directory, SCREEN_FILE);
    if (!screen || screen.size === 0) return null;
    return { screen, camera: await fileIn(this.directory, CAMERA_FILE) };
  }

  /** Drops the raw recording once it has been prepared (it would only double the space used). */
  async removeRaw(): Promise<void> {
    this.memoryRaw = null;
    await this.directory?.removeEntry(RAW_FILE).catch(() => undefined);
  }

  /** Remembers the retakes thrown away (small and rare, so a plain atomic write is fine). */
  async saveCuts(cuts: readonly TimeRange[]): Promise<void> {
    this.memoryCuts = cuts;
    if (!this.directory) return;
    try {
      const writable = await (await this.directory.getFileHandle(EDITS_FILE, { create: true })).createWritable();
      await writable.write(JSON.stringify({ cuts }));
      await writable.close();
    } catch {
      // Losing this only means a recovered recording keeps its retakes.
    }
  }

  async cuts(): Promise<readonly TimeRange[]> {
    if (!this.directory) return this.memoryCuts;
    const file = await fileIn(this.directory, EDITS_FILE);
    return file ? parseSavedCuts(await file.text()) : this.memoryCuts;
  }

  async markExported(): Promise<void> {
    await this.directory?.getFileHandle(EXPORTED_MARK, { create: true }).catch(() => undefined);
  }

  /** Lets other tabs see the session again, keeping its files. */
  release(): void {
    this.unlock();
    this.unlock = () => undefined;
  }

  /** Deletes the recording from the device. */
  async remove(): Promise<void> {
    this.memoryRaw = null;
    this.release();
    if (this.directory) await (await recordingsRoot()).removeEntry(this.id, { recursive: true }).catch(() => undefined);
  }
}

/** A recording left on the device by an earlier visit (a crash, or a tab closed mid-edit). */
export interface LeftoverRecording {
  readonly id: string;
  readonly name: string;
  readonly modified: number;
  readonly size: number;
  /** Still the raw recording: it gets repaired and prepared when opened. */
  readonly unfinished: boolean;
}

/**
 * Recordings that no open tab is using. Exported or empty ones are cleaned up
 * on the way: the person already has their video, or there is nothing to save.
 */
export async function findLeftovers(): Promise<LeftoverRecording[]> {
  if (!hasOpfs()) return [];
  const root = await recordingsRoot();
  const held = await heldLocks();
  const leftovers: LeftoverRecording[] = [];
  const entries = root as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> };
  for await (const [id, handle] of entries.entries()) {
    if (handle.kind !== 'directory' || held.has(lockName(id))) continue;
    const directory = handle as FileSystemDirectoryHandle;
    const [raw = null, screen = null, camera = null, exported = null, project = null] = await Promise.all(
      [RAW_FILE, SCREEN_FILE, CAMERA_FILE, EXPORTED_MARK, PROJECT_MARK].map((name) => fileIn(directory, name)),
    );
    // A saved project owns it: it is offered back as that project, not as a leftover.
    if (project) continue;
    const files = [raw, screen, camera].filter((file): file is File => file !== null && file.size > 0);
    if (exported || files.length === 0) {
      await root.removeEntry(id, { recursive: true }).catch(() => undefined);
      continue;
    }
    leftovers.push({
      id,
      name: displayName(id),
      modified: Math.max(...files.map((file) => file.lastModified)),
      size: files.reduce((total, file) => total + file.size, 0),
      unfinished: raw !== null && raw.size > 0,
    });
  }
  return leftovers.sort((a, b) => b.modified - a.modified);
}

/** Keeps a recording on the device for a saved project (it is no longer a leftover to offer or clean up). */
export async function keepForProject(id: string): Promise<void> {
  if (!hasOpfs()) return;
  try {
    const directory = await (await recordingsRoot()).getDirectoryHandle(id);
    await directory.getFileHandle(PROJECT_MARK, { create: true });
  } catch {
    // Gone already (or no space for an empty file): the project then asks for the video like any other.
  }
}

/** The prepared files of a recording a project keeps, or null when they are gone. */
export async function projectRecording(id: string): Promise<{ readonly screen: File; readonly camera: File | null } | null> {
  if (!hasOpfs()) return null;
  try {
    const directory = await (await recordingsRoot()).getDirectoryHandle(id);
    const screen = await fileIn(directory, SCREEN_FILE);
    return screen && screen.size > 0 ? { screen, camera: await fileIn(directory, CAMERA_FILE) } : null;
  } catch {
    return null;
  }
}

/** Deletes a recording a project kept (the project was deleted). */
export async function removeProjectRecording(id: string): Promise<void> {
  if (!hasOpfs()) return;
  await (await recordingsRoot()).removeEntry(id, { recursive: true }).catch(() => undefined);
}

/** Free space the browser grants this site, in bytes (null when it won't say). */
export async function freeSpace(): Promise<number | null> {
  const estimate = await navigator.storage?.estimate?.().catch(() => null);
  return estimate?.quota !== undefined && estimate.usage !== undefined ? estimate.quota - estimate.usage : null;
}

/** Asks the browser not to evict recordings under storage pressure (silently granted or refused). */
export async function keepPersistent(): Promise<void> {
  await navigator.storage?.persist?.().catch(() => false);
}
